/**
 * The fleet on the overview: one row per machine with its live agents and load, the busiest
 * first, so a person with many machines sees where the work is. Pure, so the order is tested.
 */
import { machineLiveness } from '@orgus/protocol';
import type { Machine, Session } from '@orgus/protocol';

export type MachineLiveness = ReturnType<typeof machineLiveness>;

export interface FleetRow {
  id: string;
  name: string;
  liveness: MachineLiveness;
  /** Sessions working and waiting on a person on this machine. */
  working: number;
  needsYou: number;
  /** Busy fraction and used memory fraction, 0..1 (0 when not reporting). */
  load: number;
  mem: number;
}

const RANK: Record<MachineLiveness, number> = {
  online: 0,
  stale: 1,
  offline: 2,
};

export function fleetRows(machines: readonly Machine[], sessions: readonly Session[], now: number): FleetRow[] {
  const counts = new Map<string, { working: number; needsYou: number }>();
  for (const s of sessions) {
    if (s.status !== 'working' && s.status !== 'waiting') continue;
    const c = counts.get(s.machineId) ?? { working: 0, needsYou: 0 };
    if (s.status === 'waiting') c.needsYou += 1;
    else c.working += 1;
    counts.set(s.machineId, c);
  }
  return machines
    .map((m) => {
      const liveness = machineLiveness(m, now);
      const c = counts.get(m.id) ?? { working: 0, needsYou: 0 };
      const on = liveness === 'online';
      return {
        id: m.id,
        name: m.name,
        liveness,
        ...c,
        load: on ? m.load : 0,
        mem: on && m.memTotal > 0 ? 1 - m.memFree / m.memTotal : 0,
      };
    })
    .sort((a, b) => RANK[a.liveness] - RANK[b.liveness] || b.needsYou - a.needsYou || b.working - a.working || b.load - a.load || a.name.localeCompare(b.name));
}
